// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import {
  GetInboxInput,
  getInbox,
  getUnreadCount,
  markAllMessagesRead,
  markMessagesRead,
  markMessagesUnread,
} from '@/notification/api/inbox';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

const INBOX_QUERY_KEY = 'notification-inbox';

// Where the filter sits in the key toInboxQueryKey builds
const INBOX_STATE_KEY_INDEX = 1;

// A message arrives without the page asking, so the list polls rather than
// waiting for the next focus. Structural sharing keeps the rendered rows
// untouched when a poll brings back what is already on screen.
const INBOX_REFETCH_INTERVAL_MS = 30_000;

// The badge count is one shared value. Three things learn it — the poll below,
// an unread listing that already counted the same messages, and every mutation
// that moves a message in or out of unread — and each writes it here rather
// than keeping a number of its own that could disagree with the others.
const UNREAD_COUNT_QUERY_KEY = 'notification-unread-count';

// The badge is the only thing that says a message arrived while somebody was
// looking at another page, so it asks more often than the list does. It reads
// one stored number rather than a page of messages.
const UNREAD_COUNT_REFETCH_INTERVAL_MS = 15_000;

/** Records what the caller last learned the unread count to be. */
function writeUnreadCount(queryClient: QueryClient, unreadCount: number) {
  queryClient.setQueryData([UNREAD_COUNT_QUERY_KEY], unreadCount);
}

/** The query key for one page of the inbox. */
// The filter a query key was built for, which says whether its page still
// belongs on screen.
function toQueriedState(queryKey: readonly unknown[] | undefined) {
  return queryKey?.[INBOX_STATE_KEY_INDEX];
}

function toInboxQueryKey(input: GetInboxInput) {
  return [
    INBOX_QUERY_KEY,
    input.state,
    input.page,
    input.limit,
    input.isSparse ?? false,
  ];
}

interface UseInboxInput extends GetInboxInput {
  // The bell fetches nothing until its popover opens
  enabled?: boolean;
}

/** Reads one page of the caller's in-app messages. */
export function useInbox(input: UseInboxInput) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: toInboxQueryKey(input),
    queryFn: async () => {
      const page = await getInbox({
        state: input.state,
        page: input.page,
        limit: input.limit,
        isSparse: input.isSparse,
      });
      // An unread listing has counted exactly what the badge asks for, so the
      // badge takes it instead of waiting out its own interval
      if (input.state === 'unread') {
        writeUnreadCount(queryClient, page.totalCount);
      }
      return page;
    },
    enabled: input.enabled ?? true,
    refetchInterval: INBOX_REFETCH_INTERVAL_MS,
    // Page and limit are part of the key, so without this a page change or a
    // load-more unmounts the rendered list and resets the reader's scroll.
    //
    // The filter is part of the key too, and holding its rows is not the same
    // courtesy: read messages would sit under the unread filter with the wrong
    // action beside them until the new page landed. So the previous page is
    // kept only while the filter it was fetched for still stands.
    placeholderData: (previous, previousQuery) =>
      toQueriedState(previousQuery?.queryKey) === input.state
        ? previous
        : undefined,
  });
}

/** Reads the caller's unread count, which the badge draws. */
export function useUnreadCount() {
  return useQuery({
    queryKey: [UNREAD_COUNT_QUERY_KEY],
    queryFn: getUnreadCount,
    refetchInterval: UNREAD_COUNT_REFETCH_INTERVAL_MS,
  });
}

/**
 * Marks messages read or unread, refreshing what the change moved.
 */
export function useInboxReadState() {
  const queryClient = useQueryClient();

  // Every one of these routes answers with the caller's new count, so the badge
  // takes it rather than paying for a second round trip to ask again
  const applyCount = async (result: { unreadCount: number }) => {
    writeUnreadCount(queryClient, result.unreadCount);
    await queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
  };

  // One message and a ticked selection are the same request, so a row action
  // sends a list of one
  const markRead = useMutationWithGlobalErrorHandler({
    mutationFn: markMessagesRead,
    errorMessage: 'The messages could not be marked read',
    onSuccess: applyCount,
  });

  const markUnread = useMutationWithGlobalErrorHandler({
    mutationFn: markMessagesUnread,
    errorMessage: 'The messages could not be marked unread',
    onSuccess: applyCount,
  });

  const markAllRead = useMutationWithGlobalErrorHandler({
    mutationFn: markAllMessagesRead,
    errorMessage: 'The messages could not be marked read',
    onSuccess: applyCount,
  });

  return { markRead, markUnread, markAllRead };
}
